import { Router } from "express";
import { db } from "@workspace/db";
import { invoicesTable } from "@workspace/db";
import { eq, desc, sql } from "drizzle-orm";
import PDFDocument from "pdfkit";
import { requireAuth, type AuthRequest } from "../lib/auth";
import { readFileSync } from "fs";
import { join } from "path";

const router = Router();

/* ─── VAT setting helper ─────────────────────────────────────────── */
async function getVatEnabled(): Promise<boolean> {
  try {
    const rows = await db.execute(sql`SELECT value FROM platform_settings WHERE key = 'vat_enabled'`);
    const value = (rows.rows?.[0] as any)?.value;
    return value === "false" ? false : true; // Default to true
  } catch {
    return true; // Default to true on error
  }
}

router.get("/invoices", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.json([]);
    const invoices = await db
      .select()
      .from(invoicesTable)
      .where(eq(invoicesTable.companyId, req.companyId))
      .orderBy(desc(invoicesTable.createdAt));
    res.json(invoices);
  } catch (err) {
    next(err);
  }
});

router.get("/invoices/:invoiceId/download", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const invoice = await db
      .select()
      .from(invoicesTable)
      .where(eq(invoicesTable.id, Number(req.params.invoiceId)))
      .limit(1);

    if (!invoice[0]) return res.status(404).json({ error: "Invoice not found" });
    if (invoice[0].companyId !== req.companyId) return res.status(403).json({ error: "Forbidden" });

    const inv = invoice[0];
    const vatEnabled = await getVatEnabled();
    const doc = new PDFDocument({ size: "A4", margin: 50, bufferPages: true, layout: "portrait" });

    res.setHeader("Content-Type", "application/pdf");
    res.setHeader("Content-Disposition", `attachment; filename="invoice-${inv.invoiceNumber}.pdf"`);
    doc.pipe(res);

    // Colors
    const colors = {
      primary: "#1B2B4B",
      accent: "#10B981",
      light: "#E5E7EB",
      gray: "#6B7280",
      dark: "#1F2937",
      white: "#FFFFFF",
    };

    // A4 dimensions: 595.28 x 841.89 points
    const pageWidth = 595;
    const pageHeight = 842;
    const margin = 50;
    const rightMargin = 50;

    // Track current Y position
    let y = 50;

    // ===== HEADER SECTION =====
    doc.rect(0, 0, pageWidth, 85).fill(colors.primary);

    // Company info on left
    doc.fillColor(colors.white).fontSize(22).font("Helvetica-Bold");
    doc.text("AI Tender Copilot", margin, y + 12);

    doc.fontSize(10).font("Helvetica");
    doc.text("Government Tender Assistant", margin, y + 30);

    doc.fontSize(8).fillColor("#9CA3AF");
    doc.text("info@munafasaai.com | https://munafasaai.com", margin, y + 44);

    // Invoice label on right
    doc.fontSize(42).fillColor("rgba(255,255,255,0.12)").font("Helvetica-Bold");
    doc.text("INVOICE", pageWidth - rightMargin, y + 12, { align: "right" });

    y = 100;

    // ===== INVOICE DETAILS SECTION =====
    doc.fillColor(colors.dark);

    // Left column
    doc.fontSize(9).fillColor(colors.gray).font("Helvetica-Bold").text("Invoice Number", margin, y);
    doc.fontSize(14).fillColor(colors.primary).font("Helvetica-Bold").text(inv.invoiceNumber, margin, y + 13);

    doc.fontSize(9).fillColor(colors.gray).font("Helvetica-Bold").text("Invoice Date", margin, y + 35);
    doc.fontSize(11).fillColor(colors.dark).font("Helvetica").text(new Date(inv.createdAt).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric"
    }), margin, y + 47);

    // Right column
    const rightColX = 300;
    doc.fontSize(9).fillColor(colors.gray).font("Helvetica-Bold").text("Bill To", rightColX, y);
    doc.fontSize(12).fillColor(colors.primary).font("Helvetica-Bold").text(inv.companyName || "N/A", rightColX, y + 13);

    if (vatEnabled) {
      doc.fontSize(9).fillColor(colors.gray).font("Helvetica-Bold").text("VAT Registration", rightColX, y + 35);
      doc.fontSize(10).fillColor(colors.dark).font("Helvetica").text("310123456700003", rightColX, y + 47);
    }

    y = 170;

    // ===== PLAN DETAILS SECTION =====
    doc.rect(margin, y, pageWidth - margin - rightMargin, 1).fill(colors.accent);
    y += 15;

    doc.fontSize(11).fillColor(colors.primary).font("Helvetica-Bold");
    doc.text(`Subscription: ${inv.planName || "N/A"} | Period: Monthly | Status: ${inv.status === "paid" ? "Paid" : inv.status}`, margin, y);

    y += 35;

    // ===== INVOICE TABLE =====
    const tableWidth = pageWidth - margin - rightMargin;
    const colDesc = 280;
    const colAmount = tableWidth - colDesc;

    // Table header
    doc.rect(margin, y, tableWidth, 25).fill(colors.primary);
    doc.fillColor(colors.white).fontSize(10).font("Helvetica-Bold");
    doc.text("Description", margin + 10, y + 16);
    doc.text("Amount", margin + colDesc + 10, y + 16, { width: colAmount - 20, align: "right" });

    // Table rows
    doc.fillColor(colors.dark).font("Helvetica").fontSize(10);
    const rowHeight = 25;

    // Row 1 - Subscription
    y += rowHeight;
    doc.rect(margin, y, tableWidth, rowHeight).fill(colors.white).stroke(colors.light);
    const displayAmount = vatEnabled ? Number(inv.amountSar || 0).toFixed(2) : Number(inv.totalAmount || 0).toFixed(2);
    doc.text(`Subscription Plan - ${inv.planName || "Plan"}`, margin + 10, y + 16);
    doc.font("Helvetica-Bold").text(`${displayAmount} SAR`, pageWidth - rightMargin - 10, y + 16, { align: "right" });

    // Row 2 - VAT (only if enabled)
    if (vatEnabled) {
      y += rowHeight;
      doc.rect(margin, y, tableWidth, rowHeight).fill("#F9FAFB").stroke(colors.light);
      doc.font("Helvetica").fontSize(10).fillColor(colors.dark);
      doc.text("VAT (Value Added Tax 15%)", margin + 10, y + 16);
      doc.font("Helvetica-Bold").text(`${Number(inv.vatAmount || 0).toFixed(2)} SAR`, pageWidth - rightMargin - 10, y + 16, { align: "right" });
    }

    y += 40;

    // ===== SUMMARY SECTION =====
    const summaryWidth = 180;
    const summaryX = pageWidth - rightMargin - summaryWidth;

    if (vatEnabled) {
      // Subtotal
      doc.rect(summaryX, y, summaryWidth, 22).fill(colors.light);
      doc.fillColor(colors.gray).fontSize(9).font("Helvetica-Bold");
      doc.text("Subtotal", summaryX + 10, y + 14);
      doc.fillColor(colors.dark).font("Helvetica-Bold").fontSize(11);
      doc.text(`${Number(inv.amountSar || 0).toFixed(2)} SAR`, summaryX + summaryWidth - 10, y + 14, { align: "right" });

      // VAT
      y += 22;
      doc.rect(summaryX, y, summaryWidth, 22).fill("white").stroke(colors.light);
      doc.fillColor(colors.gray).fontSize(9).font("Helvetica-Bold");
      doc.text("VAT (15%)", summaryX + 10, y + 14);
      doc.fillColor(colors.dark).font("Helvetica-Bold").fontSize(11);
      doc.text(`${Number(inv.vatAmount || 0).toFixed(2)} SAR`, summaryX + summaryWidth - 10, y + 14, { align: "right" });

      // Total
      y += 22;
    } else {
      // When VAT is disabled, just show total
      doc.rect(summaryX, y, summaryWidth, 30).fill(colors.primary);
      doc.fillColor(colors.white).fontSize(11).font("Helvetica-Bold");
      doc.text("TOTAL", summaryX + 10, y + 19);
      doc.fontSize(14).text(`${Number(inv.totalAmount || 0).toFixed(2)} SAR`, summaryX + summaryWidth - 10, y + 19, { align: "right" });
      y += 30;
    }

    if (vatEnabled) {
      // Total row for VAT enabled case
      doc.rect(summaryX, y, summaryWidth, 30).fill(colors.primary);
      doc.fillColor(colors.white).fontSize(11).font("Helvetica-Bold");
      doc.text("TOTAL", summaryX + 10, y + 19);
      doc.fontSize(14).text(`${Number(inv.totalAmount || 0).toFixed(2)} SAR`, summaryX + summaryWidth - 10, y + 19, { align: "right" });
    }

    y += 45;

    // ===== PAYMENT DETAILS =====
    doc.rect(margin, y, pageWidth - margin - rightMargin, 1).fill(colors.light);
    y += 12;

    doc.fontSize(10).fillColor(colors.primary).font("Helvetica-Bold");
    doc.text("Payment Details", margin, y);

    y += 18;
    doc.fontSize(9).fillColor(colors.dark).font("Helvetica");
    doc.text(`Method: Credit Card  |  Date: ${new Date(inv.createdAt).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric"
    })}  |  Status: ${inv.status === "paid" ? "Paid" : "Pending"}`, margin, y);

    y += 14;
    doc.text(`Transaction ID: ${inv.stripeInvoiceId || "N/A"}`, margin, y);

    y += 30;

    // ===== TERMS =====
    doc.rect(margin, y, pageWidth - margin - rightMargin, 1).fill(colors.light);
    y += 12;

    doc.fontSize(9).fillColor(colors.gray).font("Helvetica");
    doc.text("• Payment due within 30 days  |  • Include invoice number in reference  |  • Contact: info@munafasaai.com", margin, y);

    // ===== PAID WATERMARK =====
    if (inv.status === "paid") {
      doc.save();
      doc.rotate(-30, { origin: [pageWidth / 2, 400] });
      doc.fontSize(56).fillColor("rgba(16, 185, 129, 0.08)").font("Helvetica-Bold");
      doc.text("PAID", pageWidth / 2, 400, { align: "center" });
      doc.restore();

      // Paid stamp
      doc.save();
      doc.circle(pageWidth - rightMargin - 15, 130, 28).stroke(colors.accent).lineWidth(2);
      doc.circle(pageWidth - rightMargin - 15, 130, 24).stroke(colors.accent).lineWidth(1);
      doc.fontSize(12).fillColor(colors.accent).font("Helvetica-Bold");
      doc.text("PAID", pageWidth - rightMargin - 15, 130, { align: "center" });
      doc.restore();
    }

    // ===== FOOTER =====
    const footerY = pageHeight - 40;
    doc.rect(0, footerY, pageWidth, 40).fill(colors.primary);
    doc.fontSize(8).fillColor("#D1D5DB").font("Helvetica");
    doc.text("AI Tender Copilot Saudi | info@munafasaai.com | https://munafasaai.com", pageWidth / 2, footerY + 13, { align: "center" });
    doc.fontSize(7).fillColor("#9CA3AF");
    doc.text("Computer-generated invoice", pageWidth / 2, footerY + 26, { align: "center" });

    doc.end();
  } catch (err) {
    next(err);
  }
});

export default router;
